import { AUTO_SEND_REFUSED_MESSAGE } from "components/OtpVerification/ended-code";
import {
  failureFallbackFor,
  grantFailureMessage,
  type FailureFallback,
  type GrantFailureKind,
  type GrantMethod,
} from "./login-grant-flow";

/** How long the grant screen may wait for a text before it leads with the
 * emailed link. */
export const EMAIL_PROMOTED_AFTER_MS = 10_000;

/** Why the latest send to the channel on show did not go. */
export type SendFailure = "failed" | "unavailable" | "too_many";

/** What one channel's screen knows about its sends. `delivered` is a code or
 * link on screen, whether sent in this visit or resumed. `sending` is a send
 * to this channel in flight. `failure` holds from a failed send until the
 * next one succeeds. */
export type ChannelState = {
  method: GrantMethod;
  sending: boolean;
  delivered: boolean;
  failure: SendFailure | null;
};

/** The failure a send to `method` that ended with `kind` leaves on that
 * channel's screen. Only a text can be refused or unavailable. */
export function sendFailureOf(
  method: GrantMethod,
  kind: GrantFailureKind,
): SendFailure {
  if (method !== "sms_otp") return "failed";
  if (kind === "text_unavailable") return "unavailable";
  if (kind === "rate_limited") return "too_many";
  return "failed";
}

/** Where a failed send is told: in the screen of the channel it was sent to,
 * or under the button that asked for the other channel. */
export type SendFailureSlot = "destination" | "button";

export function sendFailureSlot(
  sent: GrantMethod,
  shown: GrantMethod,
): SendFailureSlot {
  return sent === shown ? "destination" : "button";
}

/** The way forward a send failure told under a button points to. A text
 * asked for from the emailed-link screen has an email already on its way, so
 * it never points to one. */
export function askedFailureFallback(
  asked: GrantMethod,
  emailOffered: boolean,
): FailureFallback {
  return failureFallbackFor(emailOffered && asked !== "sms_otp");
}

export function titleFor(shown: GrantMethod): string {
  return shown === "sms_otp" ? "Enter your code" : "Check your email";
}

export type DestinationPhase =
  "sending" | "resending" | "delivered" | "idle" | SendFailure;

export function destinationPhase({
  sending,
  delivered,
  failure,
}: ChannelState): DestinationPhase {
  if (sending) return delivered ? "resending" : "sending";
  if (failure !== null) return failure;
  return delivered ? "delivered" : "idle";
}

/** The sentence is `lead`, the destination in bold, then `tail`. */
export type DestinationCopy = {
  lead: string;
  tail: string;
  tone: "info" | "error";
};

const DESTINATION_COPY: Record<
  GrantMethod,
  Record<DestinationPhase, [lead: string, tail: string]>
> = {
  sms_otp: {
    sending: ["Texting a code to ", "…"],
    resending: ["Texting a new code to ", "…"],
    delivered: ["We texted a code to ", "."],
    idle: ["We'll text a code to ", "."],
    failed: ["We couldn't text ", "."],
    unavailable: ["We can't text ", "."],
    too_many: ["We can't text ", " again yet."],
  },
  email_link: {
    sending: ["Sending a sign-in link to ", "…"],
    resending: ["Sending a sign-in link to ", "…"],
    delivered: ["We sent a sign-in link to ", "."],
    idle: ["We'll send a sign-in link to ", "."],
    failed: ["We couldn't send a sign-in link to ", "."],
    unavailable: ["We couldn't send a sign-in link to ", "."],
    too_many: ["We couldn't send a sign-in link to ", "."],
  },
};

const ERROR_PHASES: readonly DestinationPhase[] = [
  "failed",
  "unavailable",
  "too_many",
];

function copyOf(method: GrantMethod, phase: DestinationPhase) {
  const [lead, tail] = DESTINATION_COPY[method][phase];
  return {
    lead,
    tail,
    tone: ERROR_PHASES.includes(phase) ? "error" : "info",
  } satisfies DestinationCopy;
}

export function destinationCopy(state: ChannelState): DestinationCopy {
  return copyOf(state.method, destinationPhase(state));
}

/** The sentence for every phase a channel's number line can be in. The slot
 * lays them over one another, so its height is the tallest of them at the
 * width and destination on screen. */
export function destinationVariants(
  method: GrantMethod,
): { phase: DestinationPhase; copy: DestinationCopy }[] {
  const phases = Object.keys(DESTINATION_COPY[method]) as DestinationPhase[];
  return phases.map((phase) => ({ phase, copy: copyOf(method, phase) }));
}

/** The line under the address on the emailed-link screen: where the link is
 * opened, or, once a send failed, the way forward. */
export function emailScreenLine(sendFailed: boolean): string {
  return sendFailed
    ? "Try again, or give us a call."
    : "Open it on this device to continue.";
}

export type ResendState = { label: string; disabled: boolean };

/** The resend control: always there, only its label and enabled state change.
 * It waits out the cooldown after a send; after a failed send it is the retry,
 * enabled at once except where retrying cannot help (texts refused for this
 * number, or the wait after too many attempts). `expired` is a code the server
 * no longer accepts, which only a new one fixes, so the wait does not apply.
 * `blocked` is a screen that cannot send at all. */
export function resendState(
  state: ChannelState,
  {
    secondsLeft,
    verifying,
    expired,
    blocked,
  }: {
    secondsLeft: number;
    verifying: boolean;
    expired: boolean;
    blocked: boolean;
  },
): ResendState {
  const plain = state.method === "sms_otp" ? "Resend code" : "Resend link";
  if (state.sending) return { label: plain, disabled: true };
  if (state.failure === "unavailable") {
    return { label: "Try again", disabled: true };
  }
  if (state.failure === "too_many") {
    const label =
      secondsLeft > 0 ? `Try again in ${secondsLeft}s` : "Try again";
    return { label, disabled: secondsLeft > 0 || blocked };
  }
  if (state.failure === "failed" || !state.delivered) {
    return { label: "Try again", disabled: verifying || blocked };
  }
  if (expired) return { label: plain, disabled: blocked };
  if (secondsLeft > 0) {
    return { label: `${plain} in ${secondsLeft}s`, disabled: true };
  }
  return { label: plain, disabled: verifying || blocked };
}

export type EmailEmphasis = "primary" | "outline";

/** How the "Email me a link" button is styled beside a text: the outline by
 * default, the primary once the text is not coming, which is a failed text
 * send, or a wait of `EMAIL_PROMOTED_AFTER_MS` with no code on screen. */
export function emailEmphasis({
  textFailed,
  textDelivered,
  elapsedMs,
}: {
  textFailed: boolean;
  textDelivered: boolean;
  elapsedMs: number;
}): EmailEmphasis {
  if (textFailed) return "primary";
  if (!textDelivered && elapsedMs >= EMAIL_PROMOTED_AFTER_MS) return "primary";
  return "outline";
}

/** The "Email me a link" button is off only while its own send is under way.
 * A text still on its way leaves it on: asking for the link replaces that
 * send. */
export function emailButtonDisabled(sending: GrantMethod | null): boolean {
  return sending === "email_link";
}

export type CallLineEmphasis = "ordinary" | "prominent";

/** The call line is the way forward, and is set larger, when a send failed
 * and the grant offers no emailed link to turn to. */
export function callLineEmphasis({
  emailOffered,
  sendFailed,
}: {
  emailOffered: boolean;
  sendFailed: boolean;
}): CallLineEmphasis {
  return !emailOffered && sendFailed ? "prominent" : "ordinary";
}

export type CodeInputState = { disabled: boolean; shimmer: boolean };

/** The code box: shimmering while the first code is on its way, disabled and
 * still while there is none, and live once a code is on screen. No failure
 * disables it; only a check in flight does. */
export function codeInputState({
  sending,
  delivered,
  verifying,
}: {
  sending: boolean;
  delivered: boolean;
  verifying: boolean;
}): CodeInputState {
  if (!delivered) return { disabled: true, shimmer: sending };
  return { disabled: verifying, shimmer: false };
}

/** A failed check of the typed code. `resend_refused` is the provider
 * refusing the code the screen asked for after the typed one ended. */
export type VerifyFailure =
  GrantFailureKind | "sign_in_failed" | "resend_refused";

const SIGN_IN_FAILED_MESSAGE =
  "Your code was accepted but we couldn't sign you in. Give us a call and we'll take it from here.";

/** The line under the code box for a failed verify; empty when none. */
export function codeMessage(
  failure: VerifyFailure | null,
  fallback: FailureFallback,
): string {
  if (failure === null) return "";
  if (failure === "sign_in_failed") return SIGN_IN_FAILED_MESSAGE;
  if (failure === "resend_refused") return AUTO_SEND_REFUSED_MESSAGE;
  return grantFailureMessage(failure, fallback);
}

export function isCodeExpired(failure: VerifyFailure | null): boolean {
  return failure === "code_expired";
}
